import { useEffect, useState } from 'react';
import { InstantSearch } from 'react-instantsearch-core';
import { useIntl } from 'react-intl';

import { isPathMatch } from '@/utils/urlUtils';

import { AlgoliaSearchInput } from '@/components/AlgoliaSearchInput';
import { Box } from '@/components/Box';
import { Icon } from '@/components/Icon';
import { SectionHeader } from '@/components/SectionHeader';
import type { ContentfulSearchOverlayFragment } from '@/graphql/contentful/api';
import { useIsMdDown } from '@/hooks/useMedia';
import { useOnRouteChange } from '@/hooks/useOnRouteChange';
import { useRouter } from '@/hooks/useRouter';
import { algoliaSearchClient } from '@/integrations/algolia/client';
import {
  applyProductConfigurationFilters,
  getIndexName,
  ProductConfigurationFilterType,
} from '@/integrations/algolia/helpers';
import { HITS_PER_PAGE } from '@/lib/const';
import { commonMessages } from '@/messages/common';
import { useRegionProvider } from '@/providers/RegionProvider';
import { searchStateActions, useSearchState } from '@/states/searchState';
import { getLocalWishlistByCountry } from '@/views/WishListView/utils';

import { Configure } from '../Configure';
import { CollectionsList, ProductsList } from './components';

import * as M from './messages';
import * as S from './SearchOverlay.styles';

type AlgoliaWrapperProps = {
  draftMode: boolean;
  placeholder?: string | null;
};

const AlgoliaWrapper = ({ draftMode, placeholder }: AlgoliaWrapperProps) => (
  <InstantSearch insights={true} searchClient={algoliaSearchClient}>
    <AlgoliaSearchInput
      clearOnUnmount
      data-test-id='SearchOverlayAlgoliaWrapper'
      draftMode={draftMode}
      placeholder={placeholder}
    />
  </InstantSearch>
);

type SearchOverlayProps = {
  cmsData?: ContentfulSearchOverlayFragment | null;
  draftMode: boolean;
  offset: number;
};

export const SearchOverlay = ({
  cmsData,
  draftMode,
  offset,
}: SearchOverlayProps) => {
  const { formatMessage } = useIntl();
  const { overlayStatus, page } = useSearchState();
  const { locale, pathname, paths, replace } = useRouter();
  const isMobileView = useIsMdDown();
  const { country } = useRegionProvider();

  const [wasOpenedOnWishlist, setWasOpenedOnWishlist] = useState(false);

  const [wishlistItems, setWishlistItems] = useState(() => {
    return getLocalWishlistByCountry(country.code);
  });

  const productConfigurationFilters = applyProductConfigurationFilters(
    ProductConfigurationFilterType.SEARCH,
    undefined,
    draftMode,
  );

  //TODO resolve conflict with Modal.tsx
  useEffect(() => {
    handleMyWishlistConditionalPageReload();

    const originalOverflow = document.body.style.overflow;

    document.body.style.overflow =
      overlayStatus === 'OPEN' ? 'hidden' : originalOverflow;

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [overlayStatus]);

  const handleMyWishlistConditionalPageReload = () => {
    if (
      overlayStatus === 'OPEN' &&
      isPathMatch(pathname, paths.myAccount.myWishlist.asPath())
    ) {
      setWasOpenedOnWishlist(true);
    }

    if (overlayStatus === 'CLOSE' && wasOpenedOnWishlist) {
      const currentWishlistItems = getLocalWishlistByCountry(country.code);
      if (
        JSON.stringify(currentWishlistItems) !== JSON.stringify(wishlistItems)
      ) {
        replace(pathname);
        setWishlistItems(currentWishlistItems);
        setWasOpenedOnWishlist(false);
      }
    }
  };

  useOnRouteChange(() => searchStateActions.changeOverlayStatus('CLOSE'), {
    eventType: 'routeChangeComplete',
  });

  const searchText = cmsData?.title ?? formatMessage(commonMessages.search);
  const showRetailer = cmsData?.displayRetailers ?? true;

  const desktopHead = (
    <>
      <SectionHeader
        color='primary'
        data-test-id='SearchOverlaySectionHeader'
        sizeDesktop={40}
        sizeMobile={34}
        text={searchText}
        textAlign='center'
      />
      <Box my='s125'>
        <AlgoliaWrapper
          draftMode={draftMode}
          placeholder={cmsData?.searchBox}
        />
        {showRetailer && (
          <S.RetailersWrapper
            data-test-id='SearchOverlayRetailersWrapper'
            onClick={() => searchStateActions.changeOverlayStatus('CLOSE')}
          >
            <Icon name='locationOutline' size='l' variant='regular' />
            <S.StyledLink href={paths.search.stores.url()}>
              {M.Retailers}
            </S.StyledLink>
          </S.RetailersWrapper>
        )}
      </Box>
      <S.SuggestedSearchText data-test-id='SearchOverlaySuggestedSearchText'>
        {M.SuggestedSearch}
      </S.SuggestedSearchText>
    </>
  );

  const mobileHead = (
    <Box my={1}>
      <AlgoliaWrapper
        data-test-id='SearchOverlayMobileHeadAlgoliaWrapper'
        draftMode={draftMode}
        placeholder={cmsData?.searchBox}
      />
    </Box>
  );

  if (overlayStatus === 'CLOSE') {
    return null;
  }

  return (
    <InstantSearch
      indexName={getIndexName('PRODUCT', locale)}
      insights={true}
      searchClient={algoliaSearchClient}
    >
      <Configure
        analyticsTags={[
          'platform:web',
          'page:search',
          'context:search-overlay',
        ]}
        filters={productConfigurationFilters}
        length={HITS_PER_PAGE * page}
        offset={0}
        queryType='prefixAll'
        typoTolerance='min'
      />
      <S.Wrapper
        data-test-id='SearchOverlayWrapper'
        isOpen={overlayStatus === 'OPEN'}
        offset={offset}
      >
        <S.Content>
          {!isMobileView && (
            <S.CloseIcon
              data-test-id='SearchOverlayCloseIcon'
              data-testid='SearchOverlayCloseIcon'
              offset={offset}
              onClick={() => searchStateActions.changeOverlayStatus('CLOSE')}
            />
          )}
          <S.SearchSection>
            {isMobileView ? mobileHead : desktopHead}
            <CollectionsList data-test-id='SearchOverlayCollectionsList' />
          </S.SearchSection>
          <ProductsList
            data={false}
            data-test-id='SearchOverlayProductsList'
            isOverlay
          />
        </S.Content>
      </S.Wrapper>
    </InstantSearch>
  );
};

SearchOverlay.displayName = 'SearchOverlay';
